import { gql } from "@apollo/client";
import React, { createContext, FunctionComponent, useContext } from "react";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import {
  getFullQuestions,
  State,
} from "src/scenes/orgAdmin/formTemplates/types/state";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { GetQuestionTypesByOrganization_custom_question_type } from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { GET_FORM_VERIFICATIONS } from "../../graphql/queries";
import * as QV from "./components/QuestionVisibility";
import { Action, useFormTemplateProcessor } from "./processor";

type ContextValue = {
  state?: State;
  dispatch?: (action: Action) => void;
  formTemplateId?: uuid;
  formTemplateVersion?: string;
  enrollmentPeriodId?: uuid;
  customQuestionTypes?: GetQuestionTypesByOrganization_custom_question_type[];
  invalidQuestionVisibility: readonly uuid[];
  grades: RD.RemoteData<Error, Grade[]>;
  verificationOptions: RD.RemoteData<Error, AF.FormVerification<AF.WithId>[]>;
};

type Grade = {
  id: string;
  value: string | null;
  label: string;
};

const EditFormTemplateContext = createContext<ContextValue>({
  state: undefined,
  dispatch: undefined,
  formTemplateId: undefined,
  formTemplateVersion: undefined,
  enrollmentPeriodId: undefined,
  customQuestionTypes: undefined,
  invalidQuestionVisibility: [],
  grades: RD.notAsked(),
  verificationOptions: RD.notAsked(),
});

type Props = {
  children: React.ReactNode;
  formTemplate: AF.FormTemplate<AF.WithId>;
  customQuestionTypes: GetQuestionTypesByOrganization_custom_question_type[];
};

export const EditFormTemplateProvider: FunctionComponent<Props> = (props) => {
  const { children, formTemplate, customQuestionTypes } = props;
  const formTemplateState = useFormTemplateProcessor(formTemplate);

  const fullQuestions = getFullQuestions(formTemplateState.state).toArray();
  const invalidQuestionVisibility =
    QV.findInvalidQuestionVisibility(fullQuestions);

  const { remoteData: gradesRD } = useRemoteDataQuery<
    GQL.GetGradesForEnrollmentPeriod,
    GQL.GetGradesForEnrollmentPeriodVariables
  >(
    gql`
      query GetGradesForEnrollmentPeriod($enrollmentPeriodId: uuid!) {
        grade_config(
          where: {
            grades: { enrollment_period_id: { _eq: $enrollmentPeriodId } }
          }
          order_by: [{ order: asc }]
        ) {
          id
          label
          value
        }
      }
    `,
    { variables: { enrollmentPeriodId: formTemplate.enrollmentPeriodId } }
  );

  const { remoteData: formVerificationsData } = useRemoteDataQuery<
    GQL.GetFormVerifications,
    GQL.GetFormVerificationsVariables
  >(GET_FORM_VERIFICATIONS, {
    variables: { formTemplateId: formTemplate.id },
    fetchPolicy: "network-only",
  });

  return (
    <EditFormTemplateContext.Provider
      value={{
        ...formTemplateState,
        customQuestionTypes,
        formTemplateId: formTemplate.id,
        formTemplateVersion: formTemplate.schemaVersion,
        enrollmentPeriodId: formTemplate.enrollmentPeriodId,
        invalidQuestionVisibility,
        grades: gradesRD.map((grades) => grades.grade_config),
        verificationOptions: formVerificationsData.map((v) =>
          v.form_verification.map((v) => ({
            id: v.id,
            label: v.label,
            is_avela_ai_assisted: v.is_avela_ai_assisted,
            is_identity_ai_assisted: v.is_identity_ai_assisted,
          }))
        ),
      }}
    >
      {children}
    </EditFormTemplateContext.Provider>
  );
};

export function useEditFormTemplateContext() {
  const context = useContext<ContextValue>(EditFormTemplateContext);

  if (context === undefined) {
    throw new Error(
      "useEditFormTemplateContext must be used within a FormTemplateEditProvider"
    );
  }

  return context;
}
